const PGAdapter = require('./../../common/pg-adapter')
const {
  getPositionalArgs,
  matchAllValuesILikePreparer,
  arrayOrStringPreparer,
  qualifiedArrayOrStringPreparer
} = require('./../../tools/sql-tools')
const { getPendingDeliveriesQuery } = require('./queries')

const DATA_SHIPMENT_TABLE_NAME = 'avocado.data_shipment'
const DATA_SHIPMENT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'created_at',
  'updated_at',
  'created_by',
  'updated_by',
  'notes',
  'destination_id',
  'origin_id',
  'planning_type',
  'order_id',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'suborder_id'
]

const DATA_SHIPMENT_VIRTUAL_COLUMNS = [
  'substring(destination_id from \'state:([^:]+)\')',
  'updated_at_gte',
  'updated_at_lte',
  'created_at_gte',
  'created_at_lte',
  'carrier_id'
]

const DATA_SHIPMENTLINE_TABLE_NAME = 'avocado.data_shipmentline'

class DataShipmentAdapter extends PGAdapter {
  static get SHIPMENT_TYPE_LAST_MILE_DELIVERY () {
    return 'last_mile_delivery'
  }

  static get SHIPMENT_TYPE_FROM_WAREHOUSE () {
    return 'from_warehouse'
  }

  static get SHIPMENT_TYPE_ALL_TYPES () {
    return 'all_types'
  }

  static get shipmentTypeFilters () {
    return {
      [this.SHIPMENT_TYPE_LAST_MILE_DELIVERY]: {
        origin_id: '%:name:%', // warehouse
        destination_id: '%:sdp:%'// Health facility
      },
      [this.SHIPMENT_TYPE_FROM_WAREHOUSE]: {
        origin_id: '%:name:%' // warehouse
      },
      [this.SHIPMENT_TYPE_ALL_TYPES]: { } // No restrictions
    }
  }

  static get allShipmentsFilter () {
    return { } // No restrictions
  }

  constructor (pgConnection, username, logger) {
    const opts = {
      getRelated: {
        'lines': {
          relatedTable: DATA_SHIPMENTLINE_TABLE_NAME,
          fk: 'shipment_id'
        }
      },
      'virtualColumns': DATA_SHIPMENT_VIRTUAL_COLUMNS
    }

    const columnPreparers = {
      origin_id: matchAllValuesILikePreparer,
      destination_id: matchAllValuesILikePreparer,
      shipment_id: arrayOrStringPreparer,
      funder_id: arrayOrStringPreparer,
      program_id: arrayOrStringPreparer,
      status: qualifiedArrayOrStringPreparer(DATA_SHIPMENT_TABLE_NAME),
      route_id: arrayOrStringPreparer, // CRITICAL: Handle route_id filtering, including empty arrays for drivers with no routes
      created_at: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."${key}">=%INDEX%`, value}],
      updated_at_gte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."updated_at">=%INDEX%`, value: value}],
      updated_at_lte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."updated_at"<=%INDEX%`, value: value}],
      created_at_gte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."created_at">=%INDEX%`, value: value}],
      created_at_lte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."created_at"<=%INDEX%`, value: value}],
      // Calculated column: default preparer without adding double quotes to the key
      'substring(destination_id from \'state:([^:]+)\')': (key, value) => [{statement: `${key}=%INDEX%`, value}],
      carrier_id: (key, value) => [{statement: `avocado.data_route."carrier_id" = %INDEX%`, value}]
    }

    super(
      pgConnection,
      DATA_SHIPMENT_TABLE_NAME,
      username,
      DATA_SHIPMENT_COLUMNS,
      undefined,
      columnPreparers,
      logger,
      opts
    )
  }

  async getList (args) {
    const { addGroupOrderId, ...pgAdapterArgs } = args
    if (addGroupOrderId) {
      pgAdapterArgs.getRelated = pgAdapterArgs.getRelated || {}
      pgAdapterArgs.getRelated['order'] = {
        relatedTable: 'avocado.data_order',
        pk: 'order_id',
        fk: 'order_id',
        fields: ['group_id']
      }
    }

    const {filter: { carrier_id: carrierId } = {}} = pgAdapterArgs
    if (carrierId) {
      pgAdapterArgs.getRelated = pgAdapterArgs.getRelated || {}
      pgAdapterArgs.tableExpression =
        `${this.tableName} left join avocado.data_route on avocado.data_route."couchdb_id" = ${this.tableName}."route_id"`
      pgAdapterArgs.useTableAsExpression = true
      pgAdapterArgs.getRelated['route'] = {
        relatedTable: 'avocado.data_route',
        fk: 'couchdb_id',
        pk: 'route_id',
        fields: ['carrier_id']
      }
    }
    return super.getList(pgAdapterArgs)
  }

  /**
   * @param {Object} param
   * @param {string} param.locationId
   * @param {number} param.weeks
   * @param {any} param.pgClient
   * @returns {Promise<{locationId: string, shipmentId: string, deliveryAmount: number, deliveryInvoiceAmount: number}[]>}
   */
  async getPendingDeliveries ({locationId, weeks, pgClient}) {
    const query = getPendingDeliveriesQuery(weeks)
    const client = pgClient || this.pgConnection
    const { rows } = await client.query(query, [locationId])
    return rows
  }

  async getShipments ({
    originId,
    destinationId,
    status = [],
    weeks = 0 // get shipments in the past n weeks
  }) {
    if (!originId && !destinationId) {
      throw new Error(`at least one of 'originId' or 'destinationId' is required`)
    }
    const params = []
    let query = `
      SELECT
        s.id,
        s.shipment_id,
        s.status,
        s.origin_id,
        s.destination_id,
        s.planning_type,
        s.order_id,
        concat('product:', sl.sku) as sku,
        sl.amount as quantity,
        s.created_at,
        s.updated_at
      FROM ${DATA_SHIPMENT_TABLE_NAME} s
      LEFT JOIN ${DATA_SHIPMENTLINE_TABLE_NAME} sl on s.id = sl.shipment_id
      WHERE
    `
    if (originId) {
      params.push(originId)
      query = query.concat(` origin_id = $${params.length}`)
    }
    if (destinationId && params.length >= 1) {
      params.push(destinationId)
      query = query.concat(` AND destination_id = $${params.length}`)
    } else if (destinationId && params.length === 0) {
      params.push(destinationId)
      query = query.concat(` destination_id = $${params.length}`)
    }
    if (status.length > 0) {
      const start = params.length + 1
      const args = getPositionalArgs(status, start)
      params.push(...status)
      query = query.concat(` AND status IN (${args})`)
    }
    if (typeof (weeks) === 'number') {
      query = query.concat(` AND s.created_at + '${weeks} week'::INTERVAL >= current_date order by s.created_at`)
    }
    const { rows } = await this.pgConnection.query(query, params)
    return rows
  }

  async getLastMileDeliveriesFilterValues (selectedFilter) {
    const tableName = 'data_shipment'
    const filteringTableColumns = [
      'route_id',
      'program_id',
      'funder_id',
      // TODO, we don't sync this prop yet to RDS
      // 'delivery_date',
      'origin_id',
      'destination_id',
      'carrier_id'
    ]

    const filteringCalculatedColumns = [
      { name: 'state', expression: 'substring(destination_id from \'state:([^:]+)\')' },
      { name: 'status', expression: `${this.tableName}.status` },
      { name: 'created_at', expression: `${this.tableName}.created_at` }
    ]

    const stateFilter = {}
    let removeDestinationIdFilter = false
    if (selectedFilter.state) {
      stateFilter['substring(destination_id from \'state:([^:]+)\')'] = selectedFilter.state
      delete selectedFilter.state
      removeDestinationIdFilter = true
    }

    // Use shipmentTypeFilters for last mile delivery filtering
    const lmdFilter = DataShipmentAdapter.shipmentTypeFilters[DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY]
    const preFilter = {...lmdFilter, ...selectedFilter, ...stateFilter}
    if (removeDestinationIdFilter) {
      delete preFilter.destination_id
    }

    return this.getColumnsUniqueValues(
      filteringTableColumns,
      filteringCalculatedColumns,
      preFilter,
      `avocado.${tableName} left join avocado.data_route r on r.couchdb_id = ${tableName}.route_id`,
      this.columnPreparers
    )
  }

  async getShipmentProgramId (shipmentId) {
    if (!shipmentId) {
      throw new Error('shipmentId required to get program id')
    }

    const { rows } = await this.pgConnection.query(`
      select
        coalesce(o.program_id, s.program_id) as id,
        replace(coalesce(o.program_id, s.program_id), 'program:', '') as name
      from avocado.data_shipment s
      inner join avocado.data_order o on s.order_id = o.order_id and s.destination_id = o.destination_id
      where s.shipment_id = $1
    `, [shipmentId])
    return rows[0]
  }

  async getVolume (shipmentIds) {
    const { rows } = await this.pgConnection.query(
      `WITH unique_skus AS (
          -- Deduplicate by lower-cased old_sku_id to prevent fan-out when the same
          -- product exists with different casing. Pick the row with the lowest id for
          -- deterministic results.
          SELECT DISTINCT ON (LOWER(old_sku_id)) old_sku_id, presentation_id
          FROM avocado.data_productsku
          WHERE _deleted IS NOT TRUE
          ORDER BY LOWER(old_sku_id), id ASC
        )
        SELECT
          s.shipment_id AS shipment_id,
          ROUND(COALESCE(SUM(COALESCE(sld.amount, sl.amount, ssld.amount, ssl.amount, 0) * COALESCE(
            -- Try to get unitVolume from manufacturer-specific product (using manufacturer extracted from batch)
            manufacturer_pp.volume::DOUBLE PRECISION,
            -- Fall back to generic product unitVolume
            generic_pp.volume::DOUBLE PRECISION,
            0
          )), 0)::NUMERIC, 6) AS total_volume
        FROM avocado.data_shipment s
        LEFT JOIN avocado.data_shipmentline sl ON sl.shipment_id = s.id
        LEFT JOIN avocado.data_shipmentline_details sld ON sld.shipment_line = sl.id
        -- Fall back to initial 'new' snapshot lines when no regular shipment lines exist.
        -- This handles newly created shipments where data_shipmentline may not be populated yet.
        LEFT JOIN LATERAL (
          SELECT ss.id AS snapshot_id
          FROM avocado.data_shipmentsnapshot ss
          WHERE ss.shipment_id = s.shipment_id
            AND ss.status = 'new'
            AND sl.id IS NULL
          ORDER BY ss.created_at ASC
          LIMIT 1
        ) first_snapshot ON true
        LEFT JOIN avocado.data_shipmentsnapshotline ssl
          ON ssl.shipment_snapshot_id = first_snapshot.snapshot_id
        LEFT JOIN avocado.data_shipmentsnapshotline_details ssld
          ON ssld.shipmentsnapshot_line = ssl.id
        -- Extract manufacturer from batch string.
        -- Expected format includes "...:manufacturer:<value>:batchNo:...".
        LEFT JOIN LATERAL (
          SELECT substring(COALESCE(sld.batch, ssld.batch) from 'manufacturer:([^:]+)') AS batch_manufacturer
        ) batch_info ON true
        -- Try to get manufacturer-specific product using extracted manufacturer
        -- LOWER on both sides to handle any case mismatch between PG and CouchDB data
        LEFT JOIN unique_skus manufacturer_ps
          ON LOWER(manufacturer_ps.old_sku_id) = LOWER(batch_info.batch_manufacturer)
          AND batch_info.batch_manufacturer IS NOT NULL
          AND batch_info.batch_manufacturer != ''
        LEFT JOIN avocado.data_productpresentation manufacturer_pp
          ON manufacturer_pp.id = manufacturer_ps.presentation_id
        -- Fall back to generic product
        LEFT JOIN unique_skus generic_ps
          ON LOWER(generic_ps.old_sku_id) = LOWER(COALESCE(sl.sku, ssl.sku))
        LEFT JOIN avocado.data_productpresentation generic_pp
          ON generic_pp.id = generic_ps.presentation_id
        WHERE s.shipment_id = ANY($1)
        GROUP BY s.shipment_id`,
      [shipmentIds]
    )
    if (!rows) {
      return []
    }
    return rows
  }

  /**
     * Retrieves last mile deliveries shipments for download based on provided filters.
     * Includes carrier name by joining with data_route and data_organisation.
     *
     * @param {Object} filters - The filter object.
     * @param {string} filters.created_at_gte - Start datetime for created_at (ISO string). Required.
     * @param {string} filters.created_at_lte - End datetime for created_at (ISO string). Required.
     * @param {string} [filters.origin_id] - Origin ID filter (can include SQL wildcards like %).
     * @param {string} [filters.destination_id] - Destination ID filter (can include SQL wildcards like %).
     * @returns {Promise<Array<Object>>} A promise that resolves to an array of shipment records.
     * @throws {Error} If created_at_gte or created_at_lte are missing.
     */
  async getLastMileDeliveriesShipmentsForDownload (filters) {
    const {
      created_at_gte: createdAtGte,
      created_at_lte: createdAtLte,
      created_at: createdAt,
      origin_id: originId,
      destination_id: destinationId,
      program_id: programId,
      carrier_id: carrierId,
      route_id: routeId,
      funder_id: funderId,
      status,
      state
    } = filters

    const hasTimeRange = createdAtGte && createdAtLte
    const hasCreatedAt = createdAt

    if (!hasTimeRange && !hasCreatedAt) {
      throw new Error('Either provide a time range (created_at_gte & created_at_lte) or a specific created_at date')
    }

    const isValidDate = (dateStr) => !isNaN(Date.parse(dateStr))

    if (hasTimeRange && (!isValidDate(createdAtGte) || !isValidDate(createdAtLte))) {
      throw new Error('Invalid date format for created_at_gte or created_at_lte')
    }

    if (hasCreatedAt && !isValidDate(createdAt)) {
      throw new Error('Invalid date format for created_at')
    }

    const mappings = {
      created_at_gte: ['s.created_at', '>='],
      created_at_lte: ['s.created_at', '<='],
      updated_at_gte: ['s.updated_at', '>='],
      updated_at_lte: ['s.updated_at', '<='],
      origin_id: ['s.origin_id', 'ILIKE'],
      destination_id: ['s.destination_id', 'ILIKE'],
      program_id: ['s.program_id', '='],
      carrier_id: ['r.carrier_id', '='],
      route_id: ['s.route_id', '='],
      funder_id: ['s.funder_id', '='],
      status: ['s.status', '='],
      state: ['substring(s.destination_id from \'state:([^:]+)\')', '=']
    }

    const buildCreatedAtCondition = (dateStr) => {
      const start = new Date(dateStr)
      start.setUTCHours(0, 0, 0, 0)

      const end = new Date(dateStr)
      end.setUTCHours(23, 59, 59, 999)

      return {
        condition: `s.created_at >= $1 AND s.created_at <= $2`,
        params: [start.toISOString(), end.toISOString()]
      }
    }

    // Apply LMD filter internally (original behavior - this method always filters for last mile deliveries)
    // The filters passed in are merged with the LMD filter, with passed filters taking precedence
    const lmdFilter = DataShipmentAdapter.shipmentTypeFilters[DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY]
    const effectiveFilters = {
      ...lmdFilter,
      ...(!hasCreatedAt && hasTimeRange && { created_at_gte: createdAtGte, created_at_lte: createdAtLte }),
      ...(originId && { origin_id: originId }),
      ...(destinationId && { destination_id: destinationId }),
      ...(programId && { program_id: programId }),
      ...(carrierId && { carrier_id: carrierId }),
      // CRITICAL: Include route_id even if it's an empty array (for drivers with no routes)
      // Empty array means "match no shipments" which is the correct behavior
      ...(routeId !== undefined && { route_id: routeId }),
      ...(funderId && { funder_id: funderId }),
      ...(status && { status }),
      ...(state && { state })
    }

    const buildQueryConditions = () => {
      const conditions = []
      const params = []

      if (hasCreatedAt) {
        const { condition, params: dateParams } = buildCreatedAtCondition(createdAt)
        conditions.push(condition)
        params.push(...dateParams)
      }

      Object.entries(effectiveFilters).forEach(([key, value]) => {
        if (!mappings[key]) return

        // CRITICAL: Allow empty arrays to pass through (they represent "match nothing" filters)
        // Empty arrays are falsy in JavaScript, but we need them for route_id filtering
        if (value === undefined || value === null) return

        const [column, operator] = mappings[key]

        if (Array.isArray(value)) {
          // Empty array means "match no rows" - this is valid for route_id filtering
          // For ILIKE operators, use ILIKE ALL to match all patterns (pattern matching)
          // For other operators (=, etc.), use = ANY for exact matches
          if (operator === 'ILIKE') {
            conditions.push(`${column} ILIKE ALL($${params.length + 1}::text[])`)
          } else {
            conditions.push(`${column} = ANY($${params.length + 1}::text[])`)
          }
          params.push(value)
        } else {
          conditions.push(`${column} ${operator} $${params.length + 1}`)
          params.push(value)
        }
      })
      return { params, whereConditions: conditions }
    }

    const { params, whereConditions } = buildQueryConditions()

    const whereClause = whereConditions.join(' AND ')

    const query = `
    WITH filtered_shipments AS (
      SELECT s.id, s.shipment_id, s.order_id
      FROM avocado.data_shipment s
      LEFT JOIN avocado.data_route r ON s.route_id = r.couchdb_id
      WHERE ${whereClause}
    ),
    order_lookup AS (
      SELECT DISTINCT ON (o.order_id)
        o.order_id,
        o.group_id
      FROM avocado.data_order o
      WHERE o.group_id IS NOT NULL
        AND o.order_id IN (SELECT order_id FROM filtered_shipments WHERE order_id IS NOT NULL)
    ),
    snapshot_dates_agg AS (
      SELECT
        ss.shipment_id,
        jsonb_object_agg(ss.status, ss.earliest_date) as snapshot_dates
      FROM (
        SELECT shipment_id, status, MIN(created_at)::text as earliest_date
        FROM avocado.data_shipmentsnapshot
        WHERE shipment_id IN (SELECT shipment_id FROM filtered_shipments)
        GROUP BY shipment_id, status
      ) ss
      GROUP BY ss.shipment_id
    ),
    driver_lookup AS (
      SELECT DISTINCT ON (shipment_id)
        shipment_id,
        created_by as driver_name
      FROM avocado.data_shipmentsnapshot
      WHERE shipment_id IN (SELECT shipment_id FROM filtered_shipments)
        AND status = 'received'
      ORDER BY shipment_id, created_at ASC
    ),
    snapshot_lines AS (
      SELECT
        ssl.shipment_snapshot_id,
        jsonb_object_agg(
          COALESCE(ssld.batch, CONCAT('product:', ssl.sku, ':manufacturer:unknown:batchNo:UNKNOWN')),
          jsonb_build_object(
            'quantity', COALESCE(ssld.amount, ssl.amount),
            'checked', ssl.checked
          )
        ) as counts
      FROM avocado.data_shipmentsnapshotline ssl
      LEFT JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
      WHERE ssl.shipment_snapshot_id IN (
        SELECT ss.id
        FROM avocado.data_shipmentsnapshot ss
        WHERE ss.shipment_id IN (SELECT shipment_id FROM filtered_shipments)
      )
      GROUP BY ssl.shipment_snapshot_id
    ),
    history_agg AS (
      SELECT
        ss.shipment_id,
        jsonb_object_agg(
          CONCAT(ss.shipment_id, ':status:', ss.status, ':agent:'),
          jsonb_build_object(
            'createdAt', ss.created_at::text,
            'createdBy', jsonb_build_object('user', ss.created_by, 'agent', ''),
            'counts', COALESCE(sl.counts, '{}'::jsonb),
            'parentDocId', ss.parent_doc_id
          )
        ) as history
      FROM avocado.data_shipmentsnapshot ss
      LEFT JOIN snapshot_lines sl ON sl.shipment_snapshot_id = ss.id
      WHERE ss.shipment_id IN (SELECT shipment_id FROM filtered_shipments)
      GROUP BY ss.shipment_id
    )
    SELECT
      s.id,
      s.shipment_id,
      s.status,
      s.origin_id,
      s.destination_id,
      s.planning_type,
      s.route_id,
      CASE
        WHEN ol.group_id IS NOT NULL AND s.suborder_id IS NOT NULL
          THEN CONCAT('type:orderSnapshot:groupId:', ol.group_id, ':orderId:', s.order_id, ':suborderId:', s.suborder_id)
        ELSE s.order_id
      END as order_id,
      s.created_at,
      s.created_by,
      s.updated_at,
      s.updated_by,
      s.notes,
      s.funder_id,
      s.program_id,
      sda.snapshot_dates,
      dl.driver_name,
      ha.history,
      r.name as route_name,
      org.name AS carrier_name,
     COALESCE(
      json_agg(
        jsonb_build_object(
          'id', sl.id,
          'created_at', sl.created_at,
          'updated_at', sl.updated_at,
          'sku', sl.sku,
          'amount', sl.amount,
          'shipment_id', sl.shipment_id,
          'order_invoicing_type', sl.order_invoicing_type,
          'origin_sku', sl.origin_sku,
          'destination_sku', sl.destination_sku
        )
        -- FILTER ensures that if the LEFT JOIN returns NULL (when shipment has no lines),
        -- we don't aggregate a NULL object into the array (avoids creating '[null]')
      ) FILTER (WHERE sl.id IS NOT NULL), '[]'
    ) AS lines
    FROM avocado.data_shipment s
    LEFT JOIN avocado.data_route r ON s.route_id = r.couchdb_id
    LEFT JOIN avocado.data_organisation org ON r.carrier_id = org.id
    LEFT JOIN avocado.data_shipmentline sl ON s.id = sl.shipment_id
    LEFT JOIN order_lookup ol ON ol.order_id = s.order_id
    LEFT JOIN snapshot_dates_agg sda ON sda.shipment_id = s.shipment_id
    LEFT JOIN driver_lookup dl ON dl.shipment_id = s.shipment_id
    LEFT JOIN history_agg ha ON ha.shipment_id = s.shipment_id
    WHERE ${whereClause}
    GROUP BY
    s.id, s.shipment_id, s.status, s.origin_id, s.destination_id,
    s.planning_type, s.route_id, s.order_id, s.created_at, s.created_by,
    s.updated_at, s.updated_by, s.notes, s.funder_id, s.program_id,
    r.name, org.name, ol.group_id, sda.snapshot_dates, dl.driver_name,
    ha.history
    ORDER BY s.created_at DESC, s.shipment_id ASC
  `
    try {
      const { rows } = await this.pgConnection.query(query, params)
      return rows
    } catch (error) {
      throw new Error(`Error executing query: ${error.message}`)
    }
  }

  /**
   * Gets shipment details for a given shipment ID. Including sku and price details
   * @param {string} shipmentId - Couch shipmentId minus status. Required.
   * @returns {Promise<Array<Object>>} A promise that resolves to an array of shipment details.
   */
  async getShipmentWithDetails (shipmentId) {
    // Includes per-line batch details so downstream integrations (e.g. Shipsy returns) can send batch-level quantities.
    const query = `
        select
          ds.shipment_number as "shipmentNumber",
          ds.origin_id as "originId",
          ds.destination_id as "destinationId",
          ds.created_at as "createdAt",
          ds.status,
          ds.planning_type as "planningType",
          COALESCE(dss2.original_parent_doc_id, dss.parent_doc_id) as "parentDocId",
          ds.is_automatic_return as "isAutomaticReturn",
          dsl.id as "shipmentLineId",
          dsl.origin_sku as "originSku",
          dsl.destination_sku as "destinationSku",
          dsl.amount,
          spd.sellprice,
          jsonb_agg(jsonb_build_object(
            'batchId', dsl_detail.batch,
            'quantity', dsl_detail.amount
          ) order by dsl_detail.batch) FILTER (WHERE dsl_detail.id IS NOT NULL) as "lineDetails",
          jsonb_build_object(
            'fsid', dlo.fsid,
            'code', dlo.code,
            'uuid', dlo.uuid,
            'classification', dlo.classification,
            'city', dlo.city,
            'name', dlo.name_of_pharmacy,
            'address', dlo.address,
            'state', dlo.state,
            'country', dlo.country,
            'status', dlo.active,
            'email', dlo.email,
            'ownerName', dlo.owners_name,
            'phoneNumber', dlo.owners_phone_number,
            'isCentral', dlo.is_central
          ) as origin,
          jsonb_build_object(
            'fsid', dld.fsid,
            'code', dld.code,
            'uuid', dld.uuid,
            'classification', dld.classification,
            'city', dld.city,
            'name', dld.name_of_pharmacy,
            'address', dld.address,
            'state', dld.state,
            'country', dld.country,
            'status', dld.active,
            'email', dld.email,
            'ownerName', dld.owners_name,
            'phoneNumber', dld.owners_phone_number,
            'isCentral', dld.is_central
          ) as destination
        from avocado.data_shipment ds
        left join avocado.data_shipmentline dsl
          on ds.id = dsl.shipment_id
        left join lateral (
          select parent_doc_id
          from avocado.data_shipmentsnapshot ss
          where ss.shipment_id = ds.shipment_id
            and ss.status = 'new'
          order by ss.created_at desc
          limit 1
        ) dss on true
        -- For returns of invoice-callback follow-up shipments, the follow-up's new snapshot
        -- carries a parent_doc_id pointing to the original delivery's shipment ID. COALESCE
        -- resolves one level up the chain so the payload always references the shipment
        -- Shipsy originally knows about, not an internally-created follow-up.
        -- split_part normalises both full snapshot IDs (containing ':status:') and bare
        -- shipment IDs to the same shipment-id form before the lookup.
        left join lateral (
          select parent_doc_id as original_parent_doc_id
          from avocado.data_shipmentsnapshot ss2
          where ss2.shipment_id = split_part(dss.parent_doc_id, ':status:', 1)
            and ss2.status = 'new'
          order by ss2.created_at desc
          limit 1
        ) dss2 on true
        left join avocado.data_shipmentline_details dsl_detail
          on dsl_detail.shipment_line = dsl.id
        left join real.sellprice_daily spd
          on (dsl.sku = spd.sku and spd.date = ds.created_at::date)
        left join avocado.data_location dlo
          on ds.origin_id = dlo.fsid
        left join avocado.data_location dld
          on ds.destination_id = dld.fsid
        where ds.shipment_id = $1
        group by
          ds.shipment_number,
          ds.origin_id,
          ds.destination_id,
          ds.created_at,
          ds.status,
          ds.planning_type,
          dss.parent_doc_id,
          dss2.original_parent_doc_id,
          ds.is_automatic_return,
          dsl.id,
          dsl.origin_sku,
          dsl.destination_sku,
          dsl.amount,
          spd.sellprice,
          dlo.fsid,
          dlo.code,
          dlo.uuid,
          dlo.classification,
          dlo.city,
          dlo.name_of_pharmacy,
          dlo.address,
          dlo.state,
          dlo.country,
          dlo.active,
          dlo.email,
          dlo.owners_name,
          dlo.owners_phone_number,
          dlo.is_central,
          dld.fsid,
          dld.code,
          dld.uuid,
          dld.classification,
          dld.city,
          dld.name_of_pharmacy,
          dld.address,
          dld.state,
          dld.country,
          dld.active,
          dld.email,
          dld.owners_name,
          dld.owners_phone_number,
          dld.is_central
      `
    try {
      const { rows } = await this.pgConnection.query(query, [shipmentId])
      return rows
    } catch (error) {
      throw new Error(`Error executing query: ${error.message}`)
    }
  }

  /**
   * Fetches unitVolume values for a list of product IDs from the native product tables.
   * Used as a building block for volume calculation when postgres line tables are empty.
   * @param {string[]} productIds - e.g. ['product:ghsc-aid0015', 'product:ghsc-aid0015h']
   * @returns {Promise<Object>} Map of { productId: unitVolume }
   */
  async getUnitVolumesForProductIds (productIds) {
    if (!productIds || productIds.length === 0) {
      return {}
    }
    const normalizedSkus = productIds.map(id => id.toLowerCase().replace(/^product:/, ''))
    const { rows } = await this.pgConnection.query(`
      SELECT DISTINCT ON (LOWER(ps.old_sku_id))
        'product:' || LOWER(ps.old_sku_id) AS product_id,
        pp.volume::DOUBLE PRECISION AS unit_volume
      FROM avocado.data_productsku ps
      JOIN avocado.data_productpresentation pp ON pp.id = ps.presentation_id
      WHERE LOWER(ps.old_sku_id) = ANY($1)
        AND ps._deleted IS NOT TRUE
        AND pp.volume IS NOT NULL
      ORDER BY LOWER(ps.old_sku_id), ps.id ASC
    `, [normalizedSkus])
    return rows.reduce((acc, row) => {
      acc[row.product_id] = row.unit_volume
      return acc
    }, {})
  }

  /**
   * Gets a shipment row by shipment_number
   * This is a new function that gets shipments by their shipment numbers.
   * For shipments with the 12 alphanumeric chars, shipment numbers are guaranteed to be unique.
   * @param {string} shipmentNumber
   * @returns {Promise<Array>}
   */
  async getShipmentByNumber (shipmentNumber) {
    const query = `
      select
        ds.*,
        dsl.sku,
        dsl.amount,
        dsl.origin_sku,
        dsl.destination_sku
      from ${DATA_SHIPMENT_TABLE_NAME} ds
      left join ${DATA_SHIPMENTLINE_TABLE_NAME} dsl
        on ds.id = dsl.shipment_id
      where ds.shipment_number = $1
    `
    const { rows } = await this.pgConnection.query(query, [shipmentNumber])
    return rows
  }

  /**
   * Gets all central warehouses
   * @returns {Promise<Array<Object>>} A promise that resolves to an array of all central warehouses with their product market code.
   */
  async getCentralWarehouses () {
    const query = `
      select
        dl.*,
        dm.product_market_code as "productMarketCode"
      from avocado.data_location dl
      left join avocado.data_market dm
        on dm.id = dl.market_id
      where is_central = true
    `
    try {
      const { rows } = await this.pgConnection.query(query)
      return rows
    } catch (error) {
      throw new Error(`Error executing query: ${error.message}`)
    }
  }
}

module.exports = DataShipmentAdapter
